import React from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';

import {useResponsive} from '../../hooks/use-responsive';

import {LgTabs, SmTabs} from '../util/Tabs';
import Notification from '../util/Notification';

import Overview from './Overview';
import Pages from './Pages';
import Sentences from './Sentences';
import {mislecAPI} from "../../util/axios";

function Document({ user, updateUser }) {
    //  Document _id
    const params = useParams();
    const _id = params.id;

    const [document, setDocument] = React.useState(null);
    const [fetched, setFetched] = React.useState(false);
    const [tab, setTab] = React.useState(0);
    const [open, setOpen] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const [severity, setSeverity] = React.useState(null);

    const { t } = useTranslation();
    const navigate = useNavigate();
    const lgUp = useResponsive('up', 'lg');

    //  Tab configuration
    const config = [
        {
            label: t('document.tabs.overview.title')
        },
        {
            label: t('document.tabs.pages.title')
        },
        {
            label: t('document.tabs.sentences.title')
        }
    ]

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    }

    const fetchDocument = async (initialLoad) => {
        if (user)
            //  Fetch document from the server
            mislecAPI.get(`/api/document/${_id}`)
                .then(res => {
                    setDocument(res.data);
                    if (initialLoad) setFetched(true);
                })
                .catch(error => {
                    //  Notify user of error
                    setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                    setSeverity('error');
                    setOpen(true);

                    if (initialLoad) setFetched(true);

                    //  Expired session
                    if (error.response)
                        if (error.response.data.detail === 'api.auth.verify.401')
                            updateUser(null);
                });
    }

    React.useEffect(() => {
        fetchDocument();
    }, [user]);

    if (!fetched) {
        if (!user) navigate('/login');
        fetchDocument(true);
    }

    const update = (data) => {
        // Send data to server
        mislecAPI.put(`/api/document/${_id}`, data)
            .then(res => {
                //  Notify user of successful update
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);
                fetchDocument();
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);

                //  Expired session
                if (error.response)
                    if (error.response.data.detail === 'api.auth.verify.401')
                        updateUser(null);
            });
    }

    return (
        <>
            <Container>
                <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
                    <Typography variant='h4'>{t('document.title')}</Typography>
                </Stack>

                {document && (
                    <>
                        <Stack direction='row' alignItems='center' justifyContent='space-between' mb={2}>
                            <Grid container alignItems='center'>
                                <Grid item xs={12} sx={{ display: 'flex !important', alignItems: 'center' }}>
                                    <img src='/assets/icons/pdf.png' alt={document.filename} style={{ height: '2rem', marginRight: '8px' }} />
                                    <Typography className='mISLec_document_title' variant='body1'>{document.filename}</Typography>
                                </Grid>
                            </Grid>
                        </Stack>

                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                {lgUp ?
                                    <LgTabs config={config} tab={tab} handleTabChange={handleTabChange} />
                                    :
                                    <SmTabs config={config} tab={tab} handleTabChange={handleTabChange} />
                                }
                                <Grid item xs={lgUp ? 10 : 12}>
                                    {tab === 0 && (
                                        <Overview document={document} updateUser={updateUser} />
                                    )}
                                    {tab === 1 && (
                                        <Pages document={document} setDocument={setDocument} update={update} />
                                    )}
                                    {tab === 2 && (
                                        <Sentences document={document} setDocument={setDocument} update={update} />
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )}
                {!document && (
                    fetched ?
                        <Grid item xs={12} variant='body2'>
                            <Typography>{t('document.noDocument')}</Typography>
                        </Grid>
                        :
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '700px' }}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                )}
            </Container>
            <Notification open={open} message={message} severity={severity} onClose={() => setOpen(false)} />
        </>
    );
}

export default Document;